/**
 * A 3D volume window with three orthogonal slice planes — the Petrel way.
 *
 * Drawn to a `<canvas>` rather than SVG. Three full planes on a 48 x 48 x 16
 * grid means about 3,800 polygons; as DOM elements that makes every
 * pergeseran sudut terasa berat, sedangkan sebagai path kanvas ia selesai
 * dalam sekali gambar.
 *
 * Draw order uses the painter's algorithm: each quad is kept with the depth of
 * its centre, sorted farthest-first, then painted over. Without that the back
 * plane bleeds through the front one, and slice planes overlapping wrongly are
 * more misleading than no picture at all.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Panel, Segmented, Slider, Toggle } from "../components/ui";
import { formatValue, scaleBar } from "../lib/colorscale";
import type { GeoModel } from "../types";

type Quad = { pts: [number, number][]; depth: number; fill: string; edge?: string };

export interface VolumeControls {
  azimuth: number;
  elevation: number;
  zoom: number;
  exaggeration: number;
  i: number;
  j: number;
  k: number;
  showI: boolean;
  showJ: boolean;
  showK: boolean;
  showWells: boolean;
  /** Log terarsir yang menempel di batang sumur; ia mati bersama batangnya. */
  showWellLogs: boolean;
  /**
   * Dua bacaan dari lubang yang sama. `raw` menggambar bacaan alatnya sendiri
   * di kedalamannya sendiri; `layers` menggambar kolom terupscale — yang
   * benar-benar diberikan kepada interpolasi.
   */
  logDetail: "raw" | "layers";
  /** Lebar log pada nilai penuh, diukur di layar. */
  logWidth: number;
  showBox: boolean;
  showColorBar: boolean;
}

export const DEFAULT_CONTROLS: VolumeControls = {
  azimuth: 38,
  elevation: 26,
  zoom: 1.1,
  exaggeration: 2.4,
  i: 24,
  j: 24,
  k: 8,
  showI: true,
  showJ: true,
  showK: true,
  showWells: true,
  showWellLogs: true,
  // Bacaan aslinya yang jadi bawaan: itu yang diukur, sedangkan kolom
  // terupscale adalah tafsirannya. Yang diukur pantas ditampilkan lebih dulu.
  logDetail: "raw",
  logWidth: 52,
  showBox: true,
  showColorBar: true,
};

/**
 * Sebesar apa kanvas volume boleh tumbuh, dan bentuk yang dipertahankannya.
 *
 * Kanvas ini dulu berukuran tetap 980 x 620. Menaikkan lebarnya saja tidak
 * membesarkan volumenya: skalanya dihitung dari `Math.min(width, height)` —
 * kotak itu harus muat pada sudut putar mana pun, jadi sisi terpendeklah yang
 * menentukan — dan yang bertambah karena lebar saja hanyalah ruang kosong di
 * kiri dan kanan gambar. Keduanya karena itu diukur dari tempat yang tersedia:
 * lebarnya dari kolomnya, tingginya dari sisa layar di bawah puncak kanvas,
 * dengan bentuk 980:620 sebagai batas atas supaya kanvas yang sangat lebar
 * tidak menjadi pita.
 *
 * `VOLUME_MAX_W` menahan penyangga gambarnya pada layar yang sangat lebar:
 * tiap piksel di sini dilukis satu per satu di CPU, dan 4K bukan kanvas
 * melainkan penantian.
 */
const VOLUME_MAX_W = 1600;
const VOLUME_MIN_W = 420;
const VOLUME_MIN_H = 380;
const VOLUME_RATIO = 980 / 620;
/** Sisa di bawah kanvas: tepi jendela bukan tempat gambar berakhir. */
const VOLUME_BOTTOM_GAP = 18;

export function Volume3D({
  model,
  property,
  painter,
  barMetres,
  controls,
  onControls,
  width: widthProp = 980,
  height: heightProp = 620,
}: {
  model: GeoModel;
  property: string;
  painter: {
    at: (value: number) => string;
    atFraction: (t: number) => string;
    lo: number;
    hi: number;
  };
  barMetres: number | null;
  controls: VolumeControls;
  onControls: (next: VolumeControls) => void;
  width?: number;
  height?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Ukuran kanvas, diukur dan bukan ditetapkan.
   *
   * Angka prop-nya tinggal sebagai jatuh-tempat: sebelum pengukuran pertama,
   * dan di lingkungan yang tidak punya `ResizeObserver` sama sekali.
   */
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const measure = () => {
      const w = Math.max(VOLUME_MIN_W, Math.min(VOLUME_MAX_W, Math.round(parent.clientWidth)));
      // Tinggi dibaca dari sisa layar di bawah puncak kanvas, bukan dari
      // tinggi induknya: induknya adalah wadah yang bergulir, dan tinggi
      // sebuah wadah yang bergulir adalah tinggi isinya — yaitu kanvas ini
      // sendiri. Mengukurnya dari sana adalah mengukur diri sendiri.
      const top = canvas.getBoundingClientRect().top;
      const room = Math.round(window.innerHeight - top - VOLUME_BOTTOM_GAP);
      const h = Math.max(VOLUME_MIN_H, Math.min(Math.round(w / VOLUME_RATIO), room));
      setBox((current) => (current && current.w === w && current.h === h ? current : { w, h }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  const width = box?.w ?? widthProp;
  const height = box?.h ?? heightProp;

  // A canvas knows nothing of CSS variables, so the colours are read off the
  // element being rendered. Writing them as fixed values would make the box
  // frame and the well labels vanish the moment the theme turns light.
  const ink = (name: string, fallback: string) => {
    const el = canvasRef.current;
    if (!el) return fallback;
    const value = getComputedStyle(el).getPropertyValue(name).trim();
    return value || fallback;
  };
  const drag = useRef<{ x: number; y: number; az: number; el: number } | null>(null);

  const { nx, ny, nz } = model.grid;
  const vol = model.properties[property] ?? [];

  const project = useMemo(() => {
    const az = (controls.azimuth * Math.PI) / 180;
    const el = (controls.elevation * Math.PI) / 180;
    const ca = Math.cos(az);
    const sa = Math.sin(az);
    const ce = Math.cos(el);
    const se = Math.sin(el);
    // The scale is chosen so the box always fits at any angle, with a loose
    // margin so well labels are not clipped while the volume is turned.
    const scale = (Math.min(width, height) / 2.05) * controls.zoom;
    const cx = width / 2;
    const cy = height / 2;
    return (gi: number, gj: number, gk: number): [number, number, number] => {
      // Grid dinormalkan ke kubus satuan berpusat di titik asal. Sumbu k
      // points downwards because layer 0 is the top marker.
      const x = gi / (nx - 1) - 0.5;
      const y = gj / (ny - 1) - 0.5;
      const z = (0.5 - gk / Math.max(1, nz - 1)) * (controls.exaggeration / 4);
      const x1 = x * ca - y * sa;
      const y1 = x * sa + y * ca;
      const y2 = y1 * ce - z * se;
      const depth = y1 * se + z * ce;
      return [cx + x1 * scale, cy + y2 * scale, depth];
    };
  }, [controls.azimuth, controls.elevation, controls.zoom, controls.exaggeration,
      nx, ny, nz, width, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const quads: Quad[] = [];
    const colour = (v: number | null) =>
      v === null || !Number.isFinite(v) ? null : painter.at(v);

    const push = (
      corners: [number, number, number][],
      value: number | null,
      edge?: string,
    ) => {
      const fill = colour(value);
      if (!fill) return;
      quads.push({
        pts: corners.map((c) => [c[0], c[1]] as [number, number]),
        depth: corners.reduce((sum, c) => sum + c[2], 0) / corners.length,
        fill,
        edge,
      });
    };

    // The K plane — a fixed horizon, spanning I and J.
    if (controls.showK) {
      const layer = vol[controls.k] ?? [];
      for (let j = 0; j < ny - 1; j++) {
        for (let i = 0; i < nx - 1; i++) {
          push(
            [
              project(i, j, controls.k), project(i + 1, j, controls.k),
              project(i + 1, j + 1, controls.k), project(i, j + 1, controls.k),
            ],
            layer[j]?.[i] ?? null,
          );
        }
      }
    }
    // The J plane — a fixed row, spanning I and K.
    if (controls.showJ) {
      for (let k = 0; k < nz - 1; k++) {
        for (let i = 0; i < nx - 1; i++) {
          push(
            [
              project(i, controls.j, k), project(i + 1, controls.j, k),
              project(i + 1, controls.j, k + 1), project(i, controls.j, k + 1),
            ],
            vol[k]?.[controls.j]?.[i] ?? null,
          );
        }
      }
    }
    // The I plane — a fixed column, spanning J and K.
    if (controls.showI) {
      for (let k = 0; k < nz - 1; k++) {
        for (let j = 0; j < ny - 1; j++) {
          push(
            [
              project(controls.i, j, k), project(controls.i, j + 1, k),
              project(controls.i, j + 1, k + 1), project(controls.i, j, k + 1),
            ],
            vol[k]?.[j]?.[controls.i] ?? null,
          );
        }
      }
    }

    quads.sort((a, b) => a.depth - b.depth);
    for (const q of quads) {
      ctx.beginPath();
      ctx.moveTo(q.pts[0][0], q.pts[0][1]);
      for (let n = 1; n < q.pts.length; n++) ctx.lineTo(q.pts[n][0], q.pts[n][1]);
      ctx.closePath();
      ctx.fillStyle = q.fill;
      ctx.fill();
      // Sedikit goresan sewarna isian menutup celah antipiksel antar segiempat;
      // without it the plane looks finely meshed.
      ctx.strokeStyle = q.fill;
      ctx.lineWidth = 0.6;
      ctx.stroke();
    }

    // Box frame and axes, drawn last so they stay legible.
    if (controls.showBox) {
      const corners: [number, number, number][][] = [];
      const c = (i: number, j: number, k: number) => project(i, j, k);
      const I = nx - 1;
      const J = ny - 1;
      const K = nz - 1;
      corners.push([c(0, 0, 0), c(I, 0, 0)], [c(I, 0, 0), c(I, J, 0)],
                   [c(I, J, 0), c(0, J, 0)], [c(0, J, 0), c(0, 0, 0)]);
      corners.push([c(0, 0, K), c(I, 0, K)], [c(I, 0, K), c(I, J, K)],
                   [c(I, J, K), c(0, J, K)], [c(0, J, K), c(0, 0, K)]);
      corners.push([c(0, 0, 0), c(0, 0, K)], [c(I, 0, 0), c(I, 0, K)],
                   [c(I, J, 0), c(I, J, K)], [c(0, J, 0), c(0, J, K)]);
      ctx.strokeStyle = ink("--overlay-line", "rgba(150,165,180,0.55)");
      ctx.lineWidth = 1;
      for (const [a, b] of corners) {
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
      }
      ctx.font = "600 11px ui-monospace, monospace";
      ctx.fillStyle = ink("--overlay-text", "rgba(210,220,230,0.9)");
      const top = project(0, 0, 0);
      const base = project(0, 0, K);
      ctx.fillText(model.top_marker, top[0] - 6, top[1] - 8);
      ctx.fillText(model.base_marker, base[0] - 6, base[1] + 16);
    }

    // Distance bar. Measured along the box's own west-east edge at the current
    // rotation, so it shrinks as the volume turns away — a bar that kept its
    // length while the box foreshortened would be measuring nothing.
    {
      const a = project(0, ny - 1, nz - 1);
      const b = project(nx - 1, ny - 1, nz - 1);
      const edgePixels = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const bar = scaleBar(model.grid.x1 - model.grid.x0, edgePixels, barMetres);
      const bx = 18;
      const by = height - 18;
      ctx.strokeStyle = ink("--overlay-text", "rgba(210,220,230,0.9)");
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + bar.pixels, by);
      ctx.moveTo(bx, by - 5);
      ctx.lineTo(bx, by + 5);
      ctx.moveTo(bx + bar.pixels, by - 5);
      ctx.lineTo(bx + bar.pixels, by + 5);
      ctx.stroke();
      ctx.font = "600 11px ui-monospace, monospace";
      ctx.fillStyle = ink("--overlay-text", "rgba(210,220,230,0.9)");
      ctx.textAlign = "left";
      ctx.fillText(bar.label, bx, by - 9);
    }

    // Wells as vertical sticks, from the top marker to the base marker —
    // dengan lognya sendiri terarsir di sepanjang lubang.
    if (controls.showWells) {
      const span = painter.hi - painter.lo || 1;
      // Yang terjauh digambar lebih dulu, sama seperti petak-petaknya: dua
      // lubang yang berjajar sepanjang garis pandang harus saling menutup,
      // bukan saling menembus.
      const placed = model.wells
        .map((w) => {
          const gi = ((w.x - model.grid.x0) / (model.grid.x1 - model.grid.x0)) * (nx - 1);
          const gj = ((w.y - model.grid.y0) / (model.grid.y1 - model.grid.y0)) * (ny - 1);
          return { w, gi, gj, depth: project(gi, gj, 0)[2] };
        })
        .sort((one, other) => one.depth - other.depth);

      for (const { w, gi, gj } of placed) {
        const a = project(gi, gj, 0);
        const b = project(gi, gj, nz - 1);

        // Log terarsir: satu segiempat per selang lapisan, selebar nilainya
        // dan sewarna nilai itu pada skala yang sedang dipakai petak-petak di
        // sekitarnya. Itulah gunanya ia ada di sini — log di lubang dan model
        // di antara lubang jadi bisa dibandingkan dengan mata, bukan dua
        // bahasa warna yang kebetulan bertetangga.
        //
        // Lebarnya diukur di layar, bukan di ruang model. Pita yang ikut
        // terpuntir mengikuti azimut akan menyusut jadi garis persis ketika
        // volumenya diputar untuk melihat lubang itu.
        //
        // Dua bacaan dari lubang yang sama, dan yang dipilih menentukan apa
        // yang bisa dilihat. `raw` adalah bacaan alatnya di kedalamannya
        // sendiri; lapisan tipis, kontak tajam dan pergantian yang lebih rapat
        // daripada satu lapisan model masih ada di sana. `layers` adalah kolom
        // terupscale yang jadi masukan interpolasi. Selisih di antara keduanya
        // adalah persis apa yang hilang saat model dibangun — dan itu tidak
        // bisa dilihat kalau yang tergambar cuma salah satunya.
        //
        // Keduanya memakai sumbu yang sama: pecahan f di sepanjang selang
        // dipetakan ke k = f × (nz − 1), jadi sampel bisa jatuh di antara dua
        // lapisan, bukan dibulatkan ke lapisan terdekat.
        const raw = w.samples?.[property];
        const nzSpan = Math.max(1, nz - 1);
        const reading = (k: number, v: number | null | undefined) =>
          v === null || v === undefined || !Number.isFinite(v) ? null : { k, v };
        const trace =
          controls.logDetail === "raw" && raw && raw.f.length > 1
            ? raw.f.map((f, n) => reading(f * nzSpan, raw.v[n]))
            : (w.columns[property] ?? []).slice(0, nz).map((v, k) => reading(k, v));

        // Log 0,15 m punya lebih banyak bacaan daripada lubangnya punya piksel,
        // dan segiempat yang lebih pendek daripada seperpiksel hanya membebani
        // gambar tanpa menambah apa pun. Yang dijarangi hanya sampel yang
        // dilewati — tidak ada yang dirata-rata, jadi yang tergambar tetap
        // bacaan yang memang diambil.
        const holePixels = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const stride = Math.max(1, Math.ceil(trace.length / Math.max(8, holePixels * 3)));
        const drawn = trace.filter((_, n) => n % stride === 0 || n === trace.length - 1);
        const last = drawn.length - 1;

        if (controls.showWellLogs && last > 0) {
          const across = (v: number) =>
            Math.max(0, Math.min(1, (v - painter.lo) / span)) * controls.logWidth;
          const edge: ([number, number] | null)[] = [];
          for (let n = 0; n < last; n++) {
            const s0 = drawn[n];
            const s1 = drawn[n + 1];
            // Selang yang salah satu ujungnya kosong ditinggalkan kosong.
            // Menjembataninya berarti menggambar log di kedalaman yang
            // kurvanya sendiri tidak pernah mencapainya.
            if (!s0 || !s1) {
              edge.push(null);
              continue;
            }
            const p0 = project(gi, gj, s0.k);
            const p1 = project(gi, gj, s1.k);
            const w0 = across(s0.v);
            const w1 = across(s1.v);
            ctx.beginPath();
            ctx.moveTo(p0[0], p0[1]);
            ctx.lineTo(p0[0] + w0, p0[1]);
            ctx.lineTo(p1[0] + w1, p1[1]);
            ctx.lineTo(p1[0], p1[1]);
            ctx.closePath();
            // Isian memakai rata-rata kedua ujung selang. Memilih ujung
            // atasnya membuat seluruh log tampak bergeser setengah lapisan.
            const fill = painter.at((s0.v + s1.v) / 2);
            ctx.fillStyle = fill;
            ctx.fill();
            // Goresan sewarna isian, sama seperti petak bidang irisannya,
            // menutup celah antipiksel di antara dua selang. Pada bacaan
            // aslinya satu selang tingginya kurang dari sepiksel, dan celah
            // itulah yang membuat arsirannya bergaris-garis dan pucat —
            // warnanya jadi berbohong tentang nilainya.
            ctx.strokeStyle = fill;
            ctx.lineWidth = 0.6;
            ctx.stroke();
            edge.push([p0[0] + w0, p0[1]]);
            if (n === last - 1) edge.push([p1[0] + w1, p1[1]]);
          }
          // Tepi nilainya digariskan supaya arsiran ikut terbaca sebagai
          // kurva. Tanpa itu beberapa lapisan yang nilainya berdekatan melebur
          // jadi satu blok warna dan bentuk lognya hilang.
          //
          // Setebal batangnya sendiri, dan itu memang disengaja: pada bacaan
          // aslinya kurva ini berkelok ratusan kali di sepanjang lubang, dan
          // segaris sepiksel yang bolak-balik secepat itu terpotong-potong
          // oleh antipikselnya sendiri sampai bentuknya sulit diikuti. Ujung
          // dan sikunya dibulatkan supaya puncak yang setajam satu sampel
          // tidak tumbuh jadi duri sepanjang tiga piksel.
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.beginPath();
          let pen = false;
          for (const point of edge) {
            if (!point) {
              pen = false;
              continue;
            }
            if (pen) ctx.lineTo(point[0], point[1]);
            else ctx.moveTo(point[0], point[1]);
            pen = true;
          }
          // Halo dulu, kurvanya kemudian — resep yang sama dengan label sumur,
          // dan alasannya sama pula. Kurva ini berjalan di atas arsiran yang
          // warnanya menyapu seluruh skala: satu tinta tunggal yang kontras di
          // ujung birunya justru nyaris lenyap di ujung kuningnya. Sepasang
          // tinta-dan-halo kontras di atas warna apa pun, jadi bentuk lognya
          // terbaca sama jelas di sepanjang lubang, bukan hanya di bagian yang
          // kebetulan gelap.
          ctx.strokeStyle = ink("--label-halo", "rgba(16,18,22,0.9)");
          ctx.lineWidth = 3.4;
          ctx.stroke();
          ctx.strokeStyle = ink("--label-ink", "#f2f4f6");
          ctx.lineWidth = 1.6;
          ctx.stroke();
          // Dikembalikan supaya petak dan bingkai berikutnya tidak mewarisi
          // sudut bulat ini.
          ctx.lineJoin = "miter";
          ctx.lineCap = "butt";
        }

        ctx.strokeStyle = ink("--overlay-stick", "rgba(255,255,255,0.92)");
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
        ctx.fillStyle = ink("--overlay-stick", "rgba(255,255,255,0.92)");
        ctx.beginPath();
        ctx.arc(a[0], a[1], 2.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = "600 10px ui-monospace, monospace";
        // Outline first, fill second: a well label must stay legible over
        // petak berwarna apa pun, terang maupun gelap.
        ctx.strokeStyle = ink("--label-halo", "rgba(16,18,22,0.9)");
        ctx.lineWidth = 3;
        ctx.strokeText(w.name, a[0] + 5, a[1] - 3);
        ctx.fillStyle = ink("--label-ink", "#f2f4f6");
        ctx.fillText(w.name, a[0] + 5, a[1] - 3);
      }
    }

    // Bar warna, di sudut seberang batang jarak.
    //
    // Rel kanan memang sudah punya satu, tetapi yang berangkat ke laporan
    // adalah potretan volumenya, dan potretan itu pergi tanpa relnya. Warna
    // tanpa angka cuma mengatakan bahwa sesuatu berbeda dari yang lain — dan
    // sejak log di lubang ikut diwarnai, bar ini juga yang memberitahu bahwa
    // keduanya memakai skala yang sama.
    if (controls.showColorBar) {
      const bw = 150;
      const bh = 10;
      const bx = width - bw - 18;
      const by = height - 24;
      const grad = ctx.createLinearGradient(bx, 0, bx + bw, 0);
      for (let stop = 0; stop <= 12; stop++) {
        grad.addColorStop(stop / 12, painter.atFraction(stop / 12));
      }
      ctx.fillStyle = grad;
      ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = ink("--overlay-line", "rgba(150,165,180,0.55)");
      ctx.lineWidth = 1;
      ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);
      ctx.font = "600 10px ui-monospace, monospace";
      ctx.fillStyle = ink("--overlay-text", "rgba(210,220,230,0.9)");
      ctx.textAlign = "left";
      ctx.fillText(formatValue(painter.lo), bx, by - 4);
      ctx.textAlign = "center";
      ctx.fillText(property, bx + bw / 2, by - 4);
      ctx.textAlign = "right";
      ctx.fillText(formatValue(painter.hi), bx + bw, by - 4);
      // Dikembalikan supaya gambar berikutnya tidak mewarisi perataan ini.
      ctx.textAlign = "left";
    }
  }, [model, property, painter, barMetres, controls, project, vol, nx, ny, nz, width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        // Piksel CSS-nya sama dengan piksel yang dilukis: penyangga gambarnya
        // dibuat sebesar ini kali dpr, dan kanvas yang diregangkan CSS adalah
        // kanvas yang kabur.
        width,
        height,
        background: "var(--canvas)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        cursor: drag.current ? "grabbing" : "grab",
        touchAction: "none",
      }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = {
          x: event.clientX, y: event.clientY,
          az: controls.azimuth, el: controls.elevation,
        };
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        const dx = event.clientX - drag.current.x;
        const dy = event.clientY - drag.current.y;
        onControls({
          ...controls,
          azimuth: drag.current.az + dx * 0.4,
          // Clamped to 5-85 degrees: at zero the volume collapses to a line,
          // at ninety it becomes an ordinary map and the 3D window loses its
          // point.
          elevation: Math.max(5, Math.min(85, drag.current.el - dy * 0.3)),
        });
      }}
      onPointerUp={() => { drag.current = null; }}
      onPointerCancel={() => { drag.current = null; }}
      onWheel={(event) => {
        onControls({
          ...controls,
          zoom: Math.max(0.4, Math.min(3, controls.zoom * (event.deltaY > 0 ? 0.92 : 1.08))),
        });
      }}
    />
  );
}

/** The slice-plane control rail — one slider per axis, as in a Petrel 3D window. */
export function VolumePanels({
  model,
  controls,
  onControls,
  depthLabel,
}: {
  model: GeoModel;
  controls: VolumeControls;
  onControls: (next: VolumeControls) => void;
  /** What layer k means — a depth on a depth box, a fraction between markers. */
  depthLabel: (layer: number) => string;
}) {
  const set = (patch: Partial<VolumeControls>) => onControls({ ...controls, ...patch });
  const { nx, ny, nz } = model.grid;

  return (
    <>
      <Panel title="Slice planes">
        <Toggle label={`Inline J — ${controls.j + 1}/${ny}`} checked={controls.showJ}
                onChange={(v) => set({ showJ: v })} />
        <Slider label="J (north-south)" value={controls.j} min={0} max={ny - 1} step={1}
                onChange={(v) => set({ j: Math.round(v) })}
                format={(v) => `${Math.round(v) + 1}`} />
        <Toggle label={`Crossline I — ${controls.i + 1}/${nx}`} checked={controls.showI}
                onChange={(v) => set({ showI: v })} />
        <Slider label="I (west-east)" value={controls.i} min={0} max={nx - 1} step={1}
                onChange={(v) => set({ i: Math.round(v) })}
                format={(v) => `${Math.round(v) + 1}`} />
        <Toggle label={`Horizon K — ${controls.k + 1}/${nz}`} checked={controls.showK}
                onChange={(v) => set({ showK: v })} />
        {/* The horizontal plane is the one people slide to look for a bed, so
            it reads as a depth rather than an index wherever the framework can
            supply one. */}
        <Slider label="Depth" value={controls.k} min={0} max={nz - 1} step={1}
                onChange={(v) => set({ k: Math.round(v) })}
                format={(v) => depthLabel(Math.round(v))} />
      </Panel>

      <Panel title="View">
        <Slider label="Azimuth" value={controls.azimuth} min={-180} max={180} step={1}
                onChange={(v) => set({ azimuth: v })} format={(v) => `${v.toFixed(0)}°`} />
        <Slider label="Elevation" value={controls.elevation} min={5} max={85} step={1}
                onChange={(v) => set({ elevation: v })} format={(v) => `${v.toFixed(0)}°`} />
        <Slider label="Vertical exaggeration" value={controls.exaggeration} min={1} max={8} step={0.2}
                onChange={(v) => set({ exaggeration: v })} format={(v) => `${v.toFixed(1)}×`} />
        <Slider label="Zoom" value={controls.zoom} min={0.4} max={3} step={0.05}
                onChange={(v) => set({ zoom: v })} format={(v) => `${v.toFixed(2)}×`} />
        <Toggle label="Box frame" checked={controls.showBox} onChange={(v) => set({ showBox: v })} />
        <Toggle label="Well sticks" checked={controls.showWells} onChange={(v) => set({ showWells: v })} />
        {/* Lognya menempel pada batangnya, jadi ia padam bersama batangnya —
            terlihat mati, bukan diam-diam tidak digambar. */}
        <Toggle label="Shaded logs in the holes"
                checked={controls.showWellLogs && controls.showWells}
                disabled={!controls.showWells}
                onChange={(v) => set({ showWellLogs: v })} />
        {/* Bacaan aslinya atau kolom yang diberikan kepada interpolasi. Ia
            duduk di sini, bersebelahan dengan sakelar lognya, karena ini bukan
            pilihan gaya gambar melainkan pilihan data mana yang sedang
            dipandang. */}
        <Dropdown
          label="DETAIL"
          compact
          width={148}
          disabled={!controls.showWells || !controls.showWellLogs}
          value={controls.logDetail}
          options={[
            { value: "raw", label: "Raw log samples", hint: "the readings, unaveraged" },
            { value: "layers", label: "Upscaled layers", hint: "what the interpolation was fed" },
          ]}
          onChange={(v) => set({ logDetail: v })}
        />
        <Slider label="Log width" value={controls.logWidth} min={8} max={64} step={1}
                onChange={(v) => set({ logWidth: Math.round(v) })}
                format={(v) => `${Math.round(v)} px`} />
        <Toggle label="Colour bar" checked={controls.showColorBar}
                onChange={(v) => set({ showColorBar: v })} />
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="ghost" onClick={() => set({ azimuth: 38, elevation: 26, zoom: 1.1 })}>
            Reset view
          </Button>
          <Button variant="ghost" onClick={() => set({ azimuth: 0, elevation: 85 })}>
            From above
          </Button>
        </div>
      </Panel>

      <Panel title="Plane presets">
        <Segmented
          value="—"
          onChange={(value) => {
            if (value === "k-only") set({ showI: false, showJ: false, showK: true });
            if (value === "ij-only") set({ showI: true, showJ: true, showK: false });
            if (value === "all") set({ showI: true, showJ: true, showK: true });
          }}
          options={[
            { value: "all", label: "All three" },
            { value: "k-only", label: "Horizon" },
            { value: "ij-only", label: "Sections" },
          ]}
        />
        <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
          Drag on the volume to rotate, scroll to zoom.
        </span>
      </Panel>
    </>
  );
}
